// @story HIR-HD3
// ReportsPage — the school's reports as blocks (owner, 29 Oct 2026: "blocks
// of this report, and when I click on that block, I should be able to see the
// report page and change it … change the date range").
//
//   /reports               one block per report: what it is, whether it is on,
//                          who gets it and when it goes
//   /reports/daily-hiring  the daily hiring report for any period (yesterday,
//                          7 days, this month, custom), who gets it, when
//
// The daily email is sent by the Reports app each morning through Messages;
// who gets it at this school (the Admin Head) is changed here.
// Data: GET /api/hiring-reports, GET /api/hiring-reports/daily-hiring.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button, Card, Icon, Input, StatusPill, Switch, Table, Tabs } from '../../ds';
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from '../../components/page';

const errText = (e, fallback) => e?.response?.data?.message || fallback;

/* ── the list ──────────────────────────────────────────────────────────── */
export default function ReportsPage() {
  const navigate = useNavigate();
  const [st, setSt] = useState({ phase: 'loading' });
  useEffect(() => {
    api.get('/hiring-reports')
      .then((r) => setSt({ phase: 'ready', reports: r.data.reports }))
      .catch((e) => setSt({ phase: 'error', msg: errText(e, 'Could not load the reports.') }));
  }, []);

  return (
    <Page>
      <PageHeader title="Reports" subtitle="The reports this school gets. Open one to see it for any period, who gets it and when it goes." />
      {st.phase === 'loading' && <Card><Loading label="Loading the reports" /></Card>}
      {st.phase === 'error' && <Note tone="warning">{st.msg}</Note>}
      {st.phase === 'ready' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {st.reports.map((r) => (
            <Card key={r.key} role="button" tabIndex={0} onClick={() => navigate(`/reports/${r.key}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/reports/${r.key}`); } }}
              style={{ cursor: 'pointer', display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 'var(--fs-body-lg)', color: 'var(--text-1)' }}>
                  <Icon name="chart-bar" /> {r.title}
                </span>
                {r.on ? <StatusPill tone="success">On</StatusPill> : <StatusPill tone="neutral">Off</StatusPill>}
              </div>
              <div style={{ color: 'var(--text-2)', fontSize: 'var(--fs-body-sm)' }}>{r.about}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
                Every {r.schedule.every} at {r.schedule.at} · to {r.recipients.filter((x) => x.on).length} of {r.recipients.length} recipient group{r.recipients.length === 1 ? '' : 's'}
              </div>
              <div style={{ color: 'var(--brand-primary-ink)', fontSize: 'var(--fs-body-sm)', fontWeight: 500 }}>Open the report →</div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

/* ── one report: the daily hiring report ───────────────────────────────── */
const iso = (d) => {
  const x = new Date(d.getTime() + 5.5 * 3600000); // the date in India
  return x.toISOString().slice(0, 10);
};
const today = () => new Date();
const addDays = (d, n) => new Date(d.getTime() + n * 86400000);
const PERIODS = [
  { value: 'yesterday', label: 'Yesterday', range: () => { const y = addDays(today(), -1); return [iso(y), iso(y)]; } },
  { value: 'today', label: 'Today', range: () => [iso(today()), iso(today())] },
  { value: '7d', label: 'Last 7 days', range: () => [iso(addDays(today(), -6)), iso(today())] },
  { value: '30d', label: 'Last 30 days', range: () => [iso(addDays(today(), -29)), iso(today())] },
  { value: 'month', label: 'This month', range: () => { const t = iso(today()); return [`${t.slice(0, 8)}01`, t]; } },
  { value: 'custom', label: 'Custom', range: null },
];
const change = (now, before) => {
  if (!before) return now ? 'none in the period before' : '';
  const d = Math.round(((now - before) / before) * 100);
  return d === 0 ? 'same as the period before' : `${d > 0 ? 'up' : 'down'} ${Math.abs(d)}% on the period before (${before})`;
};

export function DailyHiringReport() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('yesterday');
  const initial = PERIODS[0].range();
  const [from, setFrom] = useState(initial[0]);
  const [to, setTo] = useState(initial[1]);
  const [st, setSt] = useState({ phase: 'loading' });

  const pick = (value) => {
    setPeriod(value);
    const p = PERIODS.find((x) => x.value === value);
    if (p.range) { const [a, b] = p.range(); setFrom(a); setTo(b); }
  };

  const load = useCallback(() => {
    if (!from || !to) return;
    setSt((s) => ({ ...s, phase: 'loading' }));
    api.get('/hiring-reports/daily-hiring', { params: { from, to } })
      .then((r) => setSt({ phase: 'ready', data: r.data }))
      .catch((e) => setSt({ phase: 'error', msg: errText(e, 'Could not compile the report.') }));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const d = st.data;
  const f = d?.figures;
  const roleColumns = useMemo(() => [
    { key: 'title', label: 'Role', render: (r) => r.title },
    { key: 'applications', label: 'Applications', render: (r) => <span className="num" style={{ fontWeight: 600 }}>{r.applications}</span> },
  ], []);

  return (
    <Page>
      <PageHeader
        title="Daily hiring report"
        subtitle="The figures the daily email carries, for any period you choose. Counts only: no names or contact details."
        actions={<Button variant="ghost" icon={<Icon name="arrow-left" />} onClick={() => navigate('/reports')}>All reports</Button>}
      />

      <Tabs items={PERIODS.map(({ value, label }) => ({ value, label }))} value={period} onChange={pick} />
      {period === 'custom' && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <DateField id="report-from" label="From" value={from} onChange={setFrom} />
          <DateField id="report-to" label="To" value={to} onChange={setTo} />
        </div>
      )}

      {st.phase === 'loading' && !d && <Card><Loading label="Compiling the report" /></Card>}
      {st.phase === 'error' && <Note tone="warning">{st.msg}</Note>}

      {d && (
        <>
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
            {from === to ? from : `${from} to ${to}`} · {d.period.days} day{d.period.days === 1 ? '' : 's'}{st.phase === 'loading' ? ' · updating…' : ''}
          </div>
          <StatRow>
            <Stat label="New applications" value={f.applications} hint={change(f.applications, d.before.applications)} />
            <Stat label="Fee paid" value={f.feePaid} hint={f.applications ? `${Math.round((f.feePaid / f.applications) * 100)}% of them` : 'of the new applications'} />
            <Stat label="Strong (80%+)" value={f.scoreBands.strong80plus} hint="CV score 80 or more" />
            <Stat label="Roles with applications" value={d.roles.withApplications} hint={`of ${d.roles.published} on the careers page`} />
          </StatRow>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start' }}>
            <Section title="Applications by role" subtitle="in this period" padding="20px 0 8px">
              {d.roles.byRole.length
                ? <Table columns={roleColumns} rows={d.roles.byRole} getRowId={(r) => r.jobId} />
                : <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No applications in this period.</div>}
            </Section>
            <div style={{ display: 'grid', gap: 20 }}>
              <Section title="CV scoring">
                <Figures rows={[
                  ['Scored', f.scoring.scored], ['Waiting to be scored', f.scoring.pending], ['Scored zero', f.scoring.zero],
                  ['CV could not be read', f.scoring.cvUnread], ['Scoring failed', f.scoring.failed],
                ]} />
              </Section>
              <Section title="Score bands">
                <Figures rows={[['80 and above', f.scoreBands.strong80plus], ['50 to 79', f.scoreBands.mid50to79], ['1 to 49', f.scoreBands.low1to49]]} />
              </Section>
              <Section title="Where they came from">
                <Figures rows={[['Careers page', f.bySource.careersPage], ['Uploaded by HR', f.bySource.uploadedByHr], ['Talent pool', f.bySource.talentPool]]} />
              </Section>
            </div>
          </div>

          {d.roles.publishedNoApplications.length > 0 && (
            <Section title="On the careers page, no applications in this period" subtitle={`${d.roles.publishedNoApplications.length} role${d.roles.publishedNoApplications.length === 1 ? '' : 's'}`}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {d.roles.publishedNoApplications.map((t) => <StatusPill key={t} tone="neutral" dot={false}>{t}</StatusPill>)}
              </div>
            </Section>
          )}

          <Recipients report={d.report} onChanged={load} />
        </>
      )}
    </Page>
  );
}

function Figures({ rows }) {
  return (
    <div style={{ display: 'grid' }}>
      {rows.map(([label, n], i) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
          <span style={{ color: 'var(--text-2)' }}>{label}</span>
          <span className="num" style={{ fontWeight: 600, color: n ? 'var(--text-1)' : 'var(--text-muted)' }}>{n}</span>
        </div>
      ))}
    </div>
  );
}

/* A date box (the design system's Input has no type=date), drawn as one. */
function DateField({ id, label, value, onChange }) {
  return (
    <label htmlFor={id} style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' }}>
      {label}
      <input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ height: 40, padding: '0 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit' }} />
    </label>
  );
}

/* Who gets it, and when it goes. The school's Admin Head is set here; the
   leaders' list is kept by the Reports app. */
function Recipients({ report, onChanged }) {
  const head = report.recipients.find((x) => x.editableHere) || {};
  const [on, setOn] = useState(Boolean(report.on));
  const [headOn, setHeadOn] = useState(Boolean(head.on));
  const [email, setEmail] = useState(head.email || '');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const saveReport = (next) => {
    setOn(next);
    api.put('/school-settings', { reporting: { dailyDigest: next } })
      .then(() => { setMsg({ tone: 'success', text: next ? 'The daily report is on for this school.' : 'The daily report is off for this school.' }); onChanged(); })
      .catch((e) => { setOn(!next); setMsg({ tone: 'danger', text: errText(e, 'Could not change it.') }); });
  };
  const saveHead = () => {
    setBusy(true);
    api.put('/school-settings', { reporting: { adminHead: { enabled: headOn, email: email.trim() } } })
      .then(() => { setMsg({ tone: 'success', text: 'Saved. It applies from the next morning\'s report.' }); onChanged(); })
      .catch((e) => setMsg({ tone: 'danger', text: errText(e, 'Could not save.') }))
      .finally(() => setBusy(false));
  };

  return (
    <Section title="Who gets it, and when" subtitle={`every ${report.schedule.every} at ${report.schedule.at}, sent by ${report.schedule.by}`}>
      <div style={{ display: 'grid', gap: 16 }}>
        <Switch label="This school's daily report is on" checked={on} onChange={saveReport} />
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ fontWeight: 500 }}>School leaders</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>The leaders on the Reports app's list get it by email. That list is kept in the Reports app.</div>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <Switch label="Also email this school's Admin Head" checked={headOn} onChange={setHeadOn} />
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <Input id="report-admin-head" label="Admin Head's email" value={email} placeholder="adminhead@school.edu.in" onChange={(e) => setEmail(e.target.value)} />
            </div>
            <Button onClick={saveHead} loading={busy} disabled={busy || (headOn && !email.trim())}>Save</Button>
          </div>
        </div>
        {msg && <Note tone={msg.tone}>{msg.text}</Note>}
      </div>
    </Section>
  );
}
